export const securityUtils = {
  /**
   * Sanitizes user input to prevent basic XSS attacks
   * Strips HTML tags, script tags, and common event handlers
   */
  sanitizeInput: (input: string, maxLength: number = 255): string => {
    if (!input) return ''

    // 1. Convert to string and trim
    let sanitized = String(input).trim()

    // 2. Enforce max length
    if (sanitized.length > maxLength) {
      sanitized = sanitized.substring(0, maxLength)
    }

    // 3. Remove script tags and their contents
    sanitized = sanitized.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      '',
    )

    // 4. Remove all other HTML tags
    sanitized = sanitized.replace(/<\/?[^>]+(>|$)/g, '')

    // 5. Remove common event handlers (onLoad, onClick, etc)
    sanitized = sanitized.replace(/on\w+="[^"]*"/g, '')
    sanitized = sanitized.replace(/on\w+='[^']*'/g, '')
    sanitized = sanitized.replace(/on\w+=\w+/g, '')

    // 6. Remove javascript: URIs
    sanitized = sanitized.replace(/javascript:/gi, '')

    return sanitized
  },

  /**
   * Validates email format
   */
  isValidEmail: (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email)
  },

  /**
   * Validates phone number format (basic international)
   */
  isValidPhone: (phone: string): boolean => {
    const phoneRegex = /^\+?[0-9\s\-\(\)]{7,20}$/
    return phoneRegex.test(phone)
  },

  /**
   * Client-side rate limiting for form submissions
   */
  rateLimiter: {
    attempts: 0,
    lastAttemptTime: 0,

    checkLimit: (
      maxAttempts: number = 3,
      timeWindowMs: number = 60000,
    ): boolean => {
      const now = Date.now()

      // Reset if time window has passed
      if (now - securityUtils.rateLimiter.lastAttemptTime > timeWindowMs) {
        securityUtils.rateLimiter.attempts = 0
      }

      securityUtils.rateLimiter.lastAttemptTime = now
      securityUtils.rateLimiter.attempts++

      return securityUtils.rateLimiter.attempts <= maxAttempts
    },
  },

  /**
   * Prints a warning in the console to prevent Self-XSS attacks
   */
  printConsoleWarning: () => {
    setTimeout(() => {
      console.log(
        '%cStop!',
        'color: red; font-size: 50px; font-weight: bold; text-shadow: 2px 2px 0 #000;',
      )
      console.log(
        '%cThis is a browser feature intended for developers. If someone told you to copy-paste something here to enable a feature or "hack" someone\'s account, it is a scam and will give them access to your account.',
        'font-size: 16px; font-family: sans-serif;',
      )
    }, 1000)
  },

  /**
   * Framebusting to prevent Clickjacking
   */
  preventClickjacking: () => {
    try {
      if (window.top !== window.self) {
        window.top!.location = window.self.location
      }
    } catch (e) {
      // If cross-origin restrictions prevent accessing window.top
      console.error('Framebusting error:', e)
    }
  },
}
